/**
 * `<tour-welcome>`: the one-time "Do you want a short tour?" card for new
 * joiners. Nothing starts until the person clicks "Yes, show me".
 *
 * It waits for announcements to finish (they go first), asks the account
 * whether this tab may show it (`claimWelcome`), and keeps the answer.
 * Escape counts as Later.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { pendingPopups } from '../../state/announcements.ts';
import { serverFlags } from '../../state/feature-flags.ts';
import { answerWelcome, claimWelcome } from './tour-progress.ts';
import { lockedBy, startTour } from './tour-engine.ts';
import { availableTours, type Tour } from './tour-registry.ts';

/** Let the page settle first: identity, page visibility, the first paint. */
const SETTLE_MS = 1500;
/** The longest wait for the account's features, which decide the tours on offer. */
const FLAGS_WAIT_MS = 15_000;

@customElement('tour-welcome')
export class TourWelcome extends LightElement {
  @state() private open = false;
  @state() private choice = '';
  private timer: number | undefined;
  private unsub: (() => void) | null = null;
  private returnFocus: HTMLElement | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    this.timer = window.setTimeout(() => this.whenFlags(), SETTLE_MS);
    document.addEventListener('keydown', this.onKey);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.clearTimeout(this.timer);
    this.unsub?.();
    document.removeEventListener('keydown', this.onKey);
  }

  /**
   * The account's features first: before they arrive, the tours list is the
   * defaults, which leaves out tours this person can take. If they never
   * arrive (no answer from the server), go on with the defaults.
   */
  private whenFlags(): void {
    const started = Date.now();
    const poll = (): void => {
      if (Object.keys(serverFlags.value).length || Date.now() - started > FLAGS_WAIT_MS) {
        this.whenQuiet();
        return;
      }
      this.timer = window.setTimeout(poll, 250);
    };
    poll();
  }

  /** Announcements first: wait until none is waiting to pop up. */
  private whenQuiet(): void {
    if (!pendingPopups.value.length) {
      void this.offer();
      return;
    }
    this.unsub = pendingPopups.subscribe((list) => {
      if (list.length) return;
      this.unsub?.();
      this.unsub = null;
      void this.offer();
    });
  }

  private async offer(): Promise<void> {
    // No tour this person can finish: do not spend their one offer.
    const tours = availableTours();
    if (!tours.length) return;
    if (!(await claimWelcome())) return;
    this.choice = (tours.find((t) => !lockedBy(t)) ?? tours[0]!).id;
    this.returnFocus = document.activeElement as HTMLElement | null;
    this.open = true;
  }

  private onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.open) {
      e.preventDefault();
      this.answer('later');
    }
  };

  private answer(answer: 'accepted' | 'later' | 'declined'): void {
    this.open = false;
    answerWelcome(answer);
    this.returnFocus?.focus?.({ preventScroll: true });
    if (answer === 'accepted') startTour(this.choice);
  }

  override updated(): void {
    if (this.open && !this.contains(document.activeElement)) {
      this.querySelector<HTMLElement>('.tour-welcome-yes')?.focus({ preventScroll: true });
    }
  }

  private renderTour(t: Tour): TemplateResult {
    const on = t.id === this.choice;
    const first = lockedBy(t);
    return html`<button
      class="tour-option ${first ? 'is-after' : ''}"
      role="radio"
      aria-checked=${on ? 'true' : 'false'}
      ?disabled=${!!first}
      title=${first ? `Finish ${first.title} first` : ''}
      @click=${() => {
        this.choice = t.id;
      }}
    >
      <span class="tour-option-radio" aria-hidden="true"></span>
      <span class="tour-option-text">
        <strong>${t.title}</strong>
        <span>${t.description}</span>
        ${first ? html`<em class="tour-option-lock">Opens after ${first.title}</em>` : nothing}
      </span>
      <span class="tour-option-min">${t.minutes} min</span>
    </button>`;
  }

  override render(): TemplateResult | typeof nothing {
    if (!this.open) return nothing;
    return html`<div class="tour-welcome-scrim">
      <div class="tour-welcome" role="dialog" aria-modal="true" aria-labelledby="tour-welcome-title">
        <div class="tour-welcome-eyebrow">Welcome to EOS Data Explorer</div>
        <h2 id="tour-welcome-title">Do you want a short tour?</h2>
        <p>Choose one. The tour lights up the real controls, and you click them yourself. You can stop at any time.</p>
        <div class="tour-option-list" role="radiogroup" aria-label="Tours">
          ${availableTours().map((t) => this.renderTour(t))}
        </div>
        <div class="tour-welcome-acts">
          <span class="tour-welcome-note">All tours are also under Show me how, at the top.</span>
          <button class="btn tour-welcome-quiet" @click=${() => this.answer('declined')}>No thanks</button>
          <button class="btn btn-secondary" @click=${() => this.answer('later')}>Later</button>
          <button class="btn btn-primary tour-welcome-yes" @click=${() => this.answer('accepted')}>Yes, show me</button>
        </div>
      </div>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tour-welcome': TourWelcome;
  }
}
